<template>
  <b-modal
    ref="modal"
    modal-class="nw-schedule-modal"
    size="lg"
    centered
    hide-header
    hide-footer
    no-close-on-backdrop
  >
    <!-- Cabeçalho e ações ficam no corpo: AccountSetting.vue esconde .modal-footer globalmente -->
    <div class="nw-sched__header">
      <span class="nw-sched__badge">
        <feather-icon icon="CalendarIcon" size="20" />
      </span>
      <div class="nw-sched__heading">
        <h4 class="nw-sched__title">{{ $t('SEND_NEWS.Modal_Schedule_Title') }}</h4>
        <p class="nw-sched__subtitle">{{ $t('SCHEDULE_NEWS.ChooseDtHr') }}</p>
      </div>
      <button type="button" class="nw-sched__close" :aria-label="$t('SCHEDULE_NEWS.Cancel')" @click="hide()">
        <feather-icon icon="XIcon" size="18" />
      </button>
    </div>

    <div class="nw-sched__body">
      <div v-if="currentDate" class="nw-sched__current">
        <feather-icon icon="InfoIcon" size="16" />
        <span>
          {{ $t('SCHEDULE_NEWS.Current') }}
          <strong>{{ formatLong(currentDate) }}</strong>
        </span>
      </div>

      <div class="nw-sched__grid">
        <div class="nw-sched__calendar">
          <flat-pickr
            v-model="date"
            class="nw-sched__date-input"
            :config="calendarConfig"
          />
        </div>

        <div class="nw-sched__side">
          <div class="nw-sched__block">
            <label class="nw-sched__label" :for="hourInputId">{{ $t('SCHEDULE_NEWS.Time') }}</label>
            <div class="nw-sched__time">
              <feather-icon icon="ClockIcon" size="20" class="nw-sched__time-icon" />
              <cleave
                :id="hourInputId"
                v-model="hour"
                class="form-control nw-sched__time-input"
                :raw="false"
                :options="hourMask"
                placeholder="hh:mm"
                inputmode="numeric"
              />
            </div>
          </div>

          <div class="nw-sched__block">
            <span class="nw-sched__label">{{ $t('SCHEDULE_NEWS.Shortcuts') }}</span>
            <div class="nw-sched__presets">
              <button
                v-for="preset in presets"
                :key="preset.key"
                type="button"
                class="nw-sched__preset"
                @click="applyDate(preset.getDate())"
              >
                <feather-icon :icon="preset.icon" size="14" />
                {{ preset.label }}
              </button>
            </div>
          </div>

          <div class="nw-sched__summary" :class="{ 'is-invalid': !!error }">
            <span class="nw-sched__summary-icon">
              <feather-icon :icon="error ? 'AlertCircleIcon' : 'SendIcon'" size="18" />
            </span>
            <div class="nw-sched__summary-text">
              <span class="nw-sched__summary-label">
                {{ error ? $t('SCHEDULE_NEWS.Check') : $t('SCHEDULE_NEWS.Summary') }}
              </span>
              <strong class="nw-sched__summary-value">{{ error || formatLong(scheduleDate) }}</strong>
              <small v-if="!error" class="nw-sched__summary-relative">{{ formatRelative(scheduleDate) }}</small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="nw-sched__footer">
      <b-button variant="outline-secondary" @click="hide()">
        {{ $t('SCHEDULE_NEWS.Cancel') }}
      </b-button>
      <b-button variant="primary" :disabled="!!error" @click="onSubmit()">
        <feather-icon icon="ClockIcon" class="mr-50" />
        {{ $t('SCHEDULE_NEWS.Send') }}
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import { BButton, BModal } from "bootstrap-vue";
import Cleave from "vue-cleave-component";
import flatPickr from "vue-flatpickr-component";
import formatFieldHelper from "@/helpers/format-field-helper";

let uid = 0;

// Modal de agendamento compartilhado entre a edição (SendNews) e a visualização (ManageNewsView).
// Abre com show(dataAgendada?) e emite "submit" com { date: "YYYY-MM-DD", hour: "HH:mm" }
// somente quando a data/hora é válida e futura.
export default {
  name: "NwScheduleModal",
  components: {
    BButton,
    BModal,
    Cleave,
    flatPickr,
  },
  data() {
    uid += 1;
    return {
      hourInputId: `nw-schedule-hour-${uid}`,
      date: null,
      hour: null,
      currentDate: null,
      // Atualizado ao abrir e ao enviar, para "no passado" considerar a hora atual
      now: new Date(),
      hourMask: {
        time: true,
        timePattern: ["h", "m"],
      },
    };
  },
  computed: {
    calendarConfig() {
      return {
        inline: true,
        dateFormat: "Y-m-d",
        minDate: "today",
        enableTime: false,
      };
    },
    scheduleDate() {
      return formatFieldHelper.parseScheduleDate(this.date, this.hour);
    },
    error() {
      if (!this.scheduleDate) {
        return this.$t("SCHEDULE_NEWS.Invalid_Date");
      }
      if (this.scheduleDate <= this.now) {
        return this.$t("SCHEDULE_NEWS.Past_Date");
      }
      return "";
    },
    presets() {
      return [
        { key: "1h", icon: "FastForwardIcon", label: this.$t("SCHEDULE_NEWS.In_1_Hour"), getDate: () => this.addHours(1) },
        { key: "3h", icon: "FastForwardIcon", label: this.$t("SCHEDULE_NEWS.In_3_Hours"), getDate: () => this.addHours(3) },
        { key: "tomorrow", icon: "SunriseIcon", label: this.$t("SCHEDULE_NEWS.Tomorrow_Morning"), getDate: () => this.tomorrowAt(8) },
      ];
    },
  },
  methods: {
    // Noticia ja agendada (data futura) abre com a data gravada; senao sugere daqui a 1 hora
    show(currentDate) {
      this.now = new Date();
      const current = currentDate ? new Date(currentDate) : null;
      this.currentDate = current && current > this.now ? current : null;
      this.applyDate(this.currentDate || formatFieldHelper.getNextHourDate());
      this.$refs.modal.show();
    },
    hide() {
      this.$refs.modal.hide();
    },
    applyDate(dt) {
      this.date = formatFieldHelper.getDtformated(dt);
      this.hour = formatFieldHelper.getHourformated(dt);
    },
    addHours(hours) {
      return new Date(Date.now() + hours * 60 * 60 * 1000);
    },
    tomorrowAt(hours) {
      const dt = new Date();
      dt.setDate(dt.getDate() + 1);
      dt.setHours(hours, 0, 0, 0);
      return dt;
    },
    formatLong(dt) {
      if (!dt) return "";
      return new Intl.DateTimeFormat(this.$i18n.locale, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(dt);
    },
    // "em 3 horas", "amanhã", "em 5 dias"
    formatRelative(dt) {
      if (!dt || typeof Intl.RelativeTimeFormat !== "function") return "";
      const minutes = Math.round((dt - this.now) / 60000);
      const rtf = new Intl.RelativeTimeFormat(this.$i18n.locale, { numeric: "auto" });
      if (minutes < 60) return rtf.format(minutes, "minute");
      const hours = Math.round(minutes / 60);
      if (hours < 24) return rtf.format(hours, "hour");
      const startOfToday = new Date(this.now.getFullYear(), this.now.getMonth(), this.now.getDate());
      const startOfTarget = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
      return rtf.format(Math.round((startOfTarget - startOfToday) / 86400000), "day");
    },
    onSubmit() {
      this.now = new Date();
      if (this.error) {
        return;
      }
      this.hide();
      this.$emit("submit", { date: this.date, hour: this.hour });
    },
  },
};
</script>

<style lang="scss">
@import '~@core/scss/base/bootstrap-extended/include';
@import '~@core/scss/base/components/variables-dark';

.nw-schedule-modal {
  --sched-primary: #{$primary};
  --sched-primary-soft: #{rgba($primary, 0.12)};
  --sched-danger: #{$danger};
  --sched-danger-soft: #{rgba($danger, 0.1)};
  --sched-surface: #ffffff;
  --sched-surface-2: #f8f8fb;
  --sched-border: #ebe9f1;
  --sched-heading: #5e5873;
  --sched-text: #6e6b7b;
  --sched-muted: #9e9cab;

  .modal-dialog {
    max-width: 760px;
  }

  .modal-content {
    overflow: hidden;
    border: 0;
    border-radius: 12px;
    background-color: var(--sched-surface);
    box-shadow: 0 18px 50px rgba(34, 41, 47, 0.25);
  }

  .modal-body {
    padding: 0;
  }

  // ---------------------------------------------------------------- cabeçalho
  .nw-sched__header {
    display: flex;
    align-items: center;
    gap: 0.9rem;
    padding: 1.25rem 1.5rem;
    border-bottom: 1px solid var(--sched-border);
  }

  .nw-sched__badge {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border-radius: 10px;
    background-color: var(--sched-primary-soft);
    color: var(--sched-primary);
  }

  .nw-sched__heading {
    flex: 1 1 auto;
    min-width: 0;
  }

  .nw-sched__title {
    margin: 0;
    color: var(--sched-heading);
    font-size: 1.1rem;
    font-weight: 600;
  }

  .nw-sched__subtitle {
    margin: 0.1rem 0 0;
    color: var(--sched-muted);
    font-size: 0.85rem;
  }

  .nw-sched__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    padding: 0;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: var(--sched-muted);
    transition: background-color 0.15s ease, color 0.15s ease;

    &:hover,
    &:focus {
      outline: none;
      background-color: var(--sched-surface-2);
      color: var(--sched-heading);
    }
  }

  // ---------------------------------------------------------------- corpo
  .nw-sched__body {
    padding: 1.5rem;
  }

  .nw-sched__current {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1.25rem;
    padding: 0.65rem 0.9rem;
    border-left: 3px solid var(--sched-primary);
    border-radius: 6px;
    background-color: var(--sched-primary-soft);
    color: var(--sched-heading);
    font-size: 0.875rem;

    svg {
      flex: 0 0 auto;
      color: var(--sched-primary);
    }

    strong {
      display: inline-block;

      &::first-letter {
        text-transform: uppercase;
      }
    }
  }

  .nw-sched__grid {
    display: flex;
    align-items: stretch;
    gap: 1.5rem;
  }

  .nw-sched__calendar {
    flex: 0 0 auto;
    padding: 0.5rem;
    border: 1px solid var(--sched-border);
    border-radius: 10px;
    background-color: var(--sched-surface);

    // O calendário fica sempre aberto; o input de texto do flatpickr não é usado
    .nw-sched__date-input {
      display: none;
    }

    .flatpickr-calendar.inline {
      margin: 0;
      border: 0;
      box-shadow: none;
      background: transparent;
    }
  }

  .nw-sched__side {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 1.25rem;
    min-width: 0;
  }

  .nw-sched__label {
    display: block;
    margin-bottom: 0.45rem;
    color: var(--sched-heading);
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .nw-sched__time {
    position: relative;
  }

  .nw-sched__time-icon {
    position: absolute;
    top: 50%;
    left: 1rem;
    color: var(--sched-primary);
    transform: translateY(-50%);
    pointer-events: none;
  }

  .nw-sched__time-input {
    height: 56px;
    padding-left: 3rem;
    border-radius: 10px;
    font-size: 1.75rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    font-variant-numeric: tabular-nums;
  }

  .nw-sched__presets {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .nw-sched__preset {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.4rem 0.8rem;
    border: 1px solid var(--sched-border);
    border-radius: 999px;
    background-color: var(--sched-surface);
    color: var(--sched-text);
    font-size: 0.8rem;
    font-weight: 500;
    transition: border-color 0.15s ease, background-color 0.15s ease, color 0.15s ease;

    svg {
      color: var(--sched-muted);
    }

    &:hover,
    &:focus-visible {
      outline: none;
      border-color: var(--sched-primary);
      background-color: var(--sched-primary-soft);
      color: var(--sched-primary);

      svg {
        color: var(--sched-primary);
      }
    }
  }

  .nw-sched__summary {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    margin-top: auto;
    padding: 0.9rem 1rem;
    border: 1px solid var(--sched-border);
    border-radius: 10px;
    background-color: var(--sched-surface-2);

    &.is-invalid {
      border-color: var(--sched-danger);
      background-color: var(--sched-danger-soft);

      .nw-sched__summary-icon {
        background-color: var(--sched-danger);
      }

      .nw-sched__summary-value {
        color: var(--sched-danger);
      }
    }
  }

  .nw-sched__summary-icon {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background-color: var(--sched-primary);
    color: #fff;
  }

  .nw-sched__summary-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .nw-sched__summary-label {
    color: var(--sched-muted);
    font-size: 0.75rem;
  }

  .nw-sched__summary-value {
    color: var(--sched-heading);
    font-size: 0.95rem;
    line-height: 1.35;

    &::first-letter {
      text-transform: uppercase;
    }
  }

  .nw-sched__summary-relative {
    color: var(--sched-primary);
    font-weight: 500;
  }

  // ---------------------------------------------------------------- rodapé
  .nw-sched__footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 1rem 1.5rem;
    border-top: 1px solid var(--sched-border);
    background-color: var(--sched-surface-2);
  }

  // ---------------------------------------------------------------- telas menores
  @media (max-width: 767.98px) {
    .nw-sched__grid {
      flex-direction: column;
    }

    .nw-sched__calendar {
      align-self: center;
      max-width: 100%;
      overflow-x: auto;
    }
  }

  @media (max-width: 575.98px) {
    .modal-dialog {
      max-width: 100%;
      min-height: 100%;
      margin: 0;
    }

    .modal-content {
      min-height: 100vh;
      border-radius: 0;
    }

    .nw-sched__body {
      padding: 1rem;
    }

    .nw-sched__footer {
      position: sticky;
      bottom: 0;

      .btn {
        flex: 1 1 0;
      }
    }
  }
}

body.dark-layout .nw-schedule-modal {
  --sched-surface: #{$theme-dark-card-bg};
  --sched-surface-2: #{$theme-dark-body-bg};
  --sched-border: #{$theme-dark-border-color};
  --sched-heading: #{$theme-dark-headings-color};
  --sched-text: #{$theme-dark-body-color};
  --sched-muted: #8a92a6;
  --sched-primary-soft: #{rgba($primary, 0.18)};
  --sched-danger-soft: #{rgba($danger, 0.16)};

  .modal-content {
    box-shadow: 0 18px 50px rgba(0, 0, 0, 0.5);
  }
}
</style>
